import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import type {
  AcceptInvitationData,
  ValidateInvitationResponse,
} from '../types/invitation';

// Validate invitation token
export const validateInvitation = async (
  token: string
): Promise<ValidateInvitationResponse> => {
  try {
    const validateFunction = httpsCallable<
      { token: string },
      ValidateInvitationResponse
    >(functions, 'validateInvitation');
    const result = await validateFunction({ token });

    const { valid, invitation, requiresAccountCreation, error } = result.data;

    if (valid && invitation) {
      return {
        valid: true,
        requiresAccountCreation,
        invitation: {
          ...invitation,
          createdAt: new Date(invitation.createdAt),
          expiresAt: new Date(invitation.expiresAt),
        },
      };
    }

    return { valid: false, requiresAccountCreation: false, error };
  } catch (error) {
    console.error('Error validating invitation:', error);
    return {
      valid: false,
      requiresAccountCreation: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to validate invitation',
    };
  }
};

// Accept invitation and create account
export const acceptInvitation = async (
  acceptData: AcceptInvitationData
): Promise<void> => {
  try {
    const acceptFunction = httpsCallable<AcceptInvitationData>(
      functions,
      'acceptInvitation'
    );
    await acceptFunction(acceptData);
  } catch (error) {
    console.error('Error accepting invitation:', error);

    const errorMessages = {
      'functions/invalid-argument':
        error instanceof Error ? error.message : 'Invalid argument',
      'functions/already-exists':
        'An account with this email already exists. Please log in instead.',
      'functions/not-found': 'Invitation or company not found.',
      'functions/internal': 'Server error. Please try again later.',
    };

    const errorCode =
      error instanceof Error && 'code' in error ? (error.code as string) : '';
    const errorMessage =
      errorMessages[errorCode as keyof typeof errorMessages] ||
      'Failed to accept invitation';

    throw new Error(errorMessage);
  }
};
